import {
	getCurrentScope,
	onScopeDispose,
	readonly,
	ref,
	type Ref,
} from 'vue';
import { callSafe } from '../utils/functions';

export type IsMobileWatchCallback = (isMobile: boolean) => void;

export type IsMobileType = 'normal' | 'strict';

// eslint-disable-next-line import/no-mutable-exports
let isMobile = false;
// eslint-disable-next-line import/no-mutable-exports
let isMobileStrict = false;
const mobileMediaQueryList = window.matchMedia('only screen and (max-width: 767px)');
const mobileMediaQueryListLandscape = window.matchMedia('only screen and (pointer: coarse) and (max-height: 767px) and (orientation: landscape)');
const mobileMediaQueryListLandscapeNX = 'only screen and (pointer: coarse) and (max-height: calc(767px / :ratio)) and (orientation: landscape)';
const mobileMediaQueryListPortrait = window.matchMedia('only screen and (pointer: coarse) and (max-width: 767px) and (orientation: portrait)');
const mobileMediaQueryListPortraitNX = 'only screen and (pointer: coarse) and (max-width: calc(767px / :ratio)) and (orientation: portrait)';
const watchers: Record<IsMobileType, IsMobileWatchCallback[]> = {
	normal: [],
	strict: [],
};

interface UseMobileReturn {
	isMobile: Ref<boolean>;
	isMobileStrict: Ref<boolean>;
	stop: () => void;
}

function onMobileMediaQueryListChange(type: IsMobileType): void {
	if (type === 'normal') {
		const currentIsMobile = mobileMediaQueryList.matches;
		isMobile = currentIsMobile;

		// eslint-disable-next-line no-restricted-syntax
		for (const watcher of watchers.normal) {
			callSafe(() => watcher(currentIsMobile));
		}
	} else if (window.devicePixelRatio === 1) {
		const currentIsMobileStrict = (
			mobileMediaQueryListLandscape.matches
			|| mobileMediaQueryListPortrait.matches
		);
		isMobileStrict = currentIsMobileStrict;

		// eslint-disable-next-line no-restricted-syntax
		for (const watcher of watchers.strict) {
			callSafe(() => watcher(currentIsMobileStrict));
		}
	} else {
		const currentIsMobileStrict = (
			window.matchMedia(mobileMediaQueryListLandscapeNX.replace(
				/:ratio/,
				window.devicePixelRatio.toString(),
			)).matches
			|| window.matchMedia(mobileMediaQueryListPortraitNX.replace(
				/:ratio/,
				window.devicePixelRatio.toString(),
			)).matches
		);
		isMobileStrict = currentIsMobileStrict;

		// eslint-disable-next-line no-restricted-syntax
		for (const watcher of watchers.strict) {
			callSafe(() => watcher(currentIsMobileStrict));
		}
	}
}

function watch(
	callback: IsMobileWatchCallback,
	type: IsMobileType = 'normal',
): () => void {
	watchers[type].push(callback);

	return () => {
		const index = watchers[type].indexOf(callback);

		if (index > -1) {
			watchers[type].splice(
				index,
				1,
			);
		}
	};
}

function useMobile(): UseMobileReturn {
	const isMobileState = ref(isMobile);
	const isMobileStrictState = ref(isMobileStrict);
	const disposers: Array<() => void> = [
		watch((value) => {
			isMobileState.value = value;
		}),
		watch(
			(value) => {
				isMobileStrictState.value = value;
			},
			'strict',
		),
	];

	const stop = () => {
		while (disposers.length) {
			const disposer = disposers.pop();
			disposer?.();
		}
	};

	if (getCurrentScope()) {
		onScopeDispose(stop);
	}

	return {
		isMobile: readonly(isMobileState),
		isMobileStrict: readonly(isMobileStrictState),
		stop,
	};
}

mobileMediaQueryList.addEventListener(
	'change',
	() => onMobileMediaQueryListChange('normal'),
);
mobileMediaQueryListLandscape.addEventListener(
	'change',
	() => onMobileMediaQueryListChange('strict'),
);
mobileMediaQueryListPortrait.addEventListener(
	'change',
	() => onMobileMediaQueryListChange('strict'),
);

if (
	(
		window.devicePixelRatio === 1
		&& (
			mobileMediaQueryListLandscape.matches
			|| mobileMediaQueryListPortrait.matches
		)
	)
	|| (
		window.devicePixelRatio > 1
		&& (
			window.matchMedia(mobileMediaQueryListLandscapeNX.replace(
				/:ratio/,
				window.devicePixelRatio.toString(),
			)).matches
			|| window.matchMedia(mobileMediaQueryListPortraitNX.replace(
				/:ratio/,
				window.devicePixelRatio.toString(),
			)).matches
		)
	)
) {
	isMobileStrict = true;
}
if (mobileMediaQueryList.matches) {
	isMobile = true;
}

export {
	isMobile,
	isMobileStrict,
	useMobile,
	watch,
};
